const CUTOFF_HOUR = 14;
const CUTOFF_MINUTE = 0;

/**
 * @param {Date} date
 * @returns {boolean}
 */
function isWorkingDay(date) {
  const day = date.getDay();
  return day >= 1 && day <= 5;
}

/**
 * @param {Date} date
 * @returns {Date}
 */
function startOfDay(date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

/**
 * @param {Date} from
 * @returns {Date}
 */
function getNextWorkingDay(from) {
  const next = new Date(from);
  next.setDate(next.getDate() + 1);

  while (!isWorkingDay(next)) {
    next.setDate(next.getDate() + 1);
  }

  return next;
}

/**
 * @param {Date} date
 * @param {number} days
 * @returns {Date}
 */
function addWorkingDays(date, days) {
  const result = new Date(date);
  let remaining = days;

  while (remaining > 0) {
    result.setDate(result.getDate() + 1);
    if (isWorkingDay(result)) remaining -= 1;
  }

  return result;
}

/**
 * @param {Date} now
 * @returns {{ cutoff: Date, shipDate: Date }}
 */
function getShippingSchedule(now) {
  const today = startOfDay(now);
  const cutoffToday = new Date(today);
  cutoffToday.setHours(CUTOFF_HOUR, CUTOFF_MINUTE, 0, 0);

  if (isWorkingDay(now) && now < cutoffToday) {
    return { cutoff: cutoffToday, shipDate: today };
  }

  const shipDate = isWorkingDay(now) ? getNextWorkingDay(now) : getNextWorkingDay(today);
  const cutoff = new Date(shipDate);
  cutoff.setHours(CUTOFF_HOUR, CUTOFF_MINUTE, 0, 0);

  return { cutoff, shipDate };
}

/**
 * @param {number} totalSeconds
 * @returns {string}
 */
function formatCountdown(totalSeconds) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
}

class ProductShippingInfo extends HTMLElement {
  /** @type {number | undefined} */
  #interval;

  connectedCallback() {
    this.#update();
    this.#interval = window.setInterval(() => this.#update(), 1000);
  }

  disconnectedCallback() {
    if (this.#interval) window.clearInterval(this.#interval);
  }

  #update() {
    const now = new Date();
    const { cutoff, shipDate } = getShippingSchedule(now);
    const countdownEl = this.querySelector('[data-shipping-countdown]');
    const shipDateEl = this.querySelector('[data-ship-date]');
    const deliveryDateEl = this.querySelector('[data-delivery-date]');
    const deliveryMinDays = Number(this.dataset.deliveryMinDays) || 1;

    if (countdownEl) {
      const secondsLeft = Math.max(0, Math.floor((cutoff.getTime() - now.getTime()) / 1000));
      countdownEl.textContent = formatCountdown(secondsLeft);
    }

    if (shipDateEl) {
      shipDateEl.textContent = this.#formatRelativeDay(shipDate, now);
    }

    if (deliveryDateEl) {
      const deliveryDate = addWorkingDays(shipDate, deliveryMinDays);
      deliveryDateEl.textContent = this.#formatWeekday(deliveryDate);
    }
  }

  /**
   * @param {Date} date
   * @param {Date} now
   * @returns {string}
   */
  #formatRelativeDay(date, now) {
    const today = startOfDay(now);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.getTime() === today.getTime()) {
      return this.dataset.labelShipsToday || 'Today';
    }

    if (date.getTime() === tomorrow.getTime()) {
      return this.dataset.labelShipsTomorrow || 'Tomorrow';
    }

    return this.#formatWeekday(date);
  }

  /**
   * @param {Date} date
   * @returns {string}
   */
  #formatWeekday(date) {
    const locale = document.documentElement.lang || 'en';
    const formatted = new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(date);
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }
}

if (!customElements.get('product-shipping-info')) {
  customElements.define('product-shipping-info', ProductShippingInfo);
}
